// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// Custom Scripts: Local
import { sleep, getAPIBaseURL } from "../scripts/utils.js";
import { updateRecording } from "./backupRecordings.js";
import { ErrorState, trackError, UploadError, BatchUploadError, DBBackupError } from "./errorHandling.js";



// ----------------------------------------------------------------------------
//   Setup
// ----------------------------------------------------------------------------


const SERVER_URL = process.env.REACT_APP_SERVER_URL;


/** Minimum S3 multipart part size is 5 MB; use 10 MB for headroom. */
const MULTIPART_CHUNK_SIZE = 10 * 1024 * 1024;

/** Granular read size when recovering the readable prefix of a part that failed to read. */
const BLOB_SCAN_CHUNK_SIZE = 1 * 1024 * 1024;

/** Number of attempts per part before failing the whole upload. */
const PART_UPLOAD_ATTEMPTS = 3;

/** Base delay before retrying a failed part; doubles on each retry. */
const PART_RETRY_BASE_DELAY_MS = 1000;



// ----------------------------------------------------------------------------
//   Helper Functions
// ----------------------------------------------------------------------------


/**
 * Construct an Error for a failed request, flagging whether it is worth retrying
 * (network failures, timeouts, throttling and server errors).
 */
function requestError( message, status = 0, retryable = undefined ) {
  const err = new Error(message);
  err.status    = status;
  err.retryable = retryable ?? (status === 0 || status === 408 || status === 429 || status >= 500);
  return err;
}


/**
 * Read the byte range [start, end) of a blob into an ArrayBuffer.
 * Only this range is held in memory, so uploads never buffer the whole recording.
 *
 * If the read throws a NotFoundError (blob data evicted / unreadable), the range is
 * re-read in small increments to recover as much of its readable prefix as possible,
 * and `truncated` is set. Any other error is rethrown.
 *
 * Returns `{ buffer, truncated }`, where `buffer` may be empty if nothing in the range is readable.
 */
async function readBlobPart( blob, start, end, scanChunkSize = BLOB_SCAN_CHUNK_SIZE ) {

  // Fast path: read the whole part at once
  try {
    return { buffer: await blob.slice(start, end).arrayBuffer(), truncated: false };
  }
  catch (e) {
    if (e?.name !== 'NotFoundError') throw e;
  }

  // Slow path: recover the readable prefix of this part, stopping at the first unreadable segment
  const buffers = [];
  let offset = start;
  while (offset < end) {
    const scanEnd = Math.min(offset + scanChunkSize, end);
    try {
      buffers.push(await blob.slice(offset, scanEnd).arrayBuffer());
      offset = scanEnd;
    }
    catch (e) {
      if (e?.name === 'NotFoundError') break;
      throw e;
    }
  }

  // Concatenate the recovered segments
  const combined  = new Uint8Array(offset - start);
  let writeOffset = 0;
  for (const buf of buffers) {
    combined.set(new Uint8Array(buf), writeOffset);
    writeOffset += buf.byteLength;
  }

  return { buffer: combined.buffer, truncated: true };
}


/**
 * Content type of a recording, detected from its first bytes.
 * The bytes are the source of truth: Safari records MP4 while Chrome/Firefox
 * record WebM, and recordings stored before the recorder's MIME type was kept
 * have an empty Blob type. Falls back to the declared type (without codec
 * parameters, which the API doesn't accept), then to WebM.
 *
 * @param {ArrayBuffer} firstBytes - The start of the recording
 * @param {string} declaredType - The Blob's type, e.g. "video/webm;codecs=vp8,opus"
 * @returns {"video/mp4" | "video/webm"}
 */
export function detectVideoContentType( firstBytes, declaredType ) {
  const b = new Uint8Array(firstBytes, 0, Math.min(8, firstBytes.byteLength));

  // MP4 / QuickTime: box size, then "ftyp"
  if (b.length >= 8 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return 'video/mp4';

  // WebM / Matroska: EBML header
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return 'video/webm';

  const baseType = (declaredType || '').split(';')[0].trim().toLowerCase();
  return baseType === 'video/mp4' ? 'video/mp4' : 'video/webm';
}


/**
 * Request a WakeLock for the screen, returning `null` if the request fails.
 */
async function getOptionalWakeLock() {

  // Request a WakeLock for the screen and return it
  try {
    return await navigator.wakeLock.request("screen");
  }

  // If the WakeLock request fails, skip it
  catch (err) {
    console.log(`Failed to obtain WakeLock: ${err.name}, ${err.message}`);
    return null;
  }
}



// ----------------------------------------------------------------------------
//   Uploads - Single Request
// ----------------------------------------------------------------------------


/**
 * Request an AWS signed URL from the API.
 * Returns an object containing:
 *   - `url`: The signed AWS URL to upload to
 *   - `filename`: The name of the file in S3.
 */
async function getUploadURL( recording ) {

  // Construct the route in the API
  const apiRoute = getAPIBaseURL("upload-url");

  // Add local file title, if specified
  if ( recording.title ) {
    apiRoute.searchParams.append("title", recording.title);
  }

  // Add content type, if specified
  if ( recording.blob?.type ) {
    apiRoute.searchParams.append("contentType", recording.blob.type);
  }
  else {
    apiRoute.searchParams.append("contentType", 'video/webm');
  }

  // Fetch signed URL from API
  try {
    const apiResponse = await fetch(
      apiRoute.toString(),
      {
        credentials: "include",
      }
    )

    if (!apiResponse.ok) {
      throw new Error('Unable to construct upload URL');
    }

    return await apiResponse.json();
  }

  // Intercept error, print where it came from, then propagate
  catch (e) {
    console.error('Unable to construct upload URL');
    throw e;
  }
}



// ----------------------------------------------------------------------------
//   Uploads - Multipart Requests
// ----------------------------------------------------------------------------


/**
 * Step 1: Initiate an S3 multipart upload.
 * Returns { uploadId, filename }.
 */
async function initiateMultipartUpload( recording, contentType ) {

  const apiRoute = getAPIBaseURL("multipart-upload/start");

  if ( recording.title ) {
    apiRoute.searchParams.append("title", recording.title);
  }

  apiRoute.searchParams.append("contentType", contentType || 'video/webm');

  const response = await fetch( apiRoute.toString(), { credentials: "include", method: 'POST' } );

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to initiate multipart upload: ${response.status} ${errorText}`);
  }

  return await response.json(); // { uploadId, filename }
}


/**
 * Step 2: Get a presigned URL for a single part.
 * Returns the signed URL string.
 */
async function getPartUploadURL( filename, uploadId, partNumber ) {

  const apiRoute = getAPIBaseURL("multipart-upload/part-url");
  apiRoute.searchParams.append("filename",   filename);
  apiRoute.searchParams.append("uploadId",   uploadId);
  apiRoute.searchParams.append("partNumber", partNumber);

  let response;
  try {
    response = await fetch( apiRoute.toString(), { credentials: "include" } );
  }
  catch (err) {
    // Network failure
    throw requestError(`Failed to get presigned URL for part ${partNumber}: ${err?.message}`);
  }

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw requestError(`Failed to get presigned URL for part ${partNumber}: ${response.status} ${errorText}`, response.status);
  }

  const { url } = await response.json();
  return url;
}


/**
 * Step 3: Complete the multipart upload.
 * `parts` is an array of { ETag, PartNumber }.
 * `truncated` signals that the uploaded data is a partial recovery of the original blob.
 * `computedSize` / `recoveredSize` are byte counts reported when truncated, stored as S3 tags.
 */
async function completeMultipartUpload( filename, uploadId, parts, truncated = false, computedSize = null, recoveredSize = null ) {

  const apiRoute = getAPIBaseURL("multipart-upload/complete");

  const response = await fetch( apiRoute.toString(), {
    method:      'POST',
    credentials: 'include',
    headers:     { 'Content-Type': 'application/json' },
    body:        JSON.stringify({ filename, uploadId, parts, truncated, computedSize, recoveredSize }),
  });

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to complete multipart upload: ${response.status} ${errorText}`);
  }
}


/**
 * Abort an in-progress multipart upload, freeing any uploaded parts.
 * Throws if the request fails or the API returns an error status.
 */
async function abortMultipartUpload( filename, uploadId ) {

  const apiRoute = getAPIBaseURL("multipart-upload/abort");

  const response = await fetch( apiRoute.toString(), {
    method:      'POST',
    credentials: 'include',
    headers:     { 'Content-Type': 'application/json' },
    body:        JSON.stringify({ filename, uploadId }),
  });

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to abort multipart upload: ${response.status} ${errorText}`);
  }
}


/**
 * Upload a single blob chunk to a presigned S3 URL via XHR.
 * Returns the ETag from the S3 response (needed for CompleteMultipartUpload).
 *
 * NOTE: The S3 bucket's CORS config must include `ETag` in `ExposeHeaders`
 *       so the client can read the ETag from the response.
 */
function uploadPart( url, chunk, partNumber, contentType, onProgress ) {
  return new Promise((resolve, reject) => {

    // Init request object & set basic parameters
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = 5 * 60 * 1000; // 5 minutes per part

    // Attach the reject function to all the possible error events
    // Network errors and timeouts are worth retrying; an explicit abort is not.
    const handleErr = (event) => reject(requestError(
      `Part ${partNumber} upload failed: ${event?.type ?? 'error'}${ xhr.status ? ` (HTTP ${xhr.status})` : '' }`,
      xhr.status,
      event?.type !== 'abort',
    ));
    xhr.addEventListener('abort',   handleErr);
    xhr.addEventListener('error',   handleErr);
    xhr.addEventListener('timeout', handleErr);

    // If a progress function was provided, attach it as well
    if (onProgress) {
      xhr.upload.addEventListener('progress', ({ loaded }) => onProgress(loaded));
    }

    // Main callback - completion
    // NOTE: Not guaranteed to be a success
    xhr.addEventListener('load', () => {

      // Check for error responses
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(requestError(`Part ${partNumber} upload failed with HTTP ${xhr.status}: ${ xhr.responseText || xhr.statusText }`, xhr.status));
        return;
      }

      // Retrieve the ETag from the response
      const etag = xhr.getResponseHeader('ETag');

      // If ETag is missing, the S3 bucket CORS config is likely missing ExposeHeaders: [ETag]
      if (!etag) {
        reject(requestError(`Part ${partNumber} response missing ETag header. Check S3 bucket CORS configuration.`, xhr.status, false));
        return;
      }

      resolve(etag);
    });

    // Upload the chunk
    xhr.send(chunk);
  });
}


/**
 * Upload a single part, retrying transient failures (network errors, timeouts,
 * 5xx / 429 responses) with exponential backoff. A fresh presigned URL is
 * requested for each attempt.
 */
async function uploadPartWithRetry( filename, uploadId, partNumber, chunk, contentType, onProgress, { attempts = PART_UPLOAD_ATTEMPTS, retryDelayMs = PART_RETRY_BASE_DELAY_MS, verbose = false } = {} ) {
  for (let attempt = 1; ; attempt++) {
    try {
      const partUrl = await getPartUploadURL( filename, uploadId, partNumber );
      return await uploadPart( partUrl, chunk, partNumber, contentType, onProgress );
    }
    catch (err) {
      if (!err?.retryable || attempt >= attempts) throw err;

      const delay = retryDelayMs * 2 ** (attempt - 1);
      if (verbose) console.warn(`Part ${partNumber} attempt ${attempt}/${attempts} failed (${err.message}); retrying in ${delay}ms.`);

      // Reset this part's progress before retrying
      if (onProgress) onProgress(0);
      await sleep(delay);
    }
  }
}



// ----------------------------------------------------------------------------
//   Main Logic
// ----------------------------------------------------------------------------


/**
 * Upload a recording blob to S3 via a multipart upload.
 *
 * The blob is read and uploaded one part at a time, so only about one part
 * (10 MB) is held in memory regardless of the recording's length. If part of the
 * blob is unreadable (NotFoundError, e.g. evicted from IndexedDB), the readable
 * prefix is uploaded and the object is tagged as truncated server-side.
 *
 * Args:
 *   - recording: The local database entry with the blob to upload
 *   - onProgress: An optional callback to run with progress updates `(loaded, total)`.
 *   - verbose: Whether to log progress to the console.
 *   - options: Optional overrides `{ partSize, retryDelayMs, attempts }` (mainly for testing).
 */
export async function uploadRecording(recording, onProgress, verbose=false, options={}) {
  if (verbose) console.log("Uploading...")

  const {
    partSize     = MULTIPART_CHUNK_SIZE,
    retryDelayMs = PART_RETRY_BASE_DELAY_MS,
    attempts     = PART_UPLOAD_ATTEMPTS,
  } = options;

  const blob     = recording.blob;

  // Declare vars to make available in error
  let uploadId, filename;

  // If recording blob has no data, abort here
  if (!blob) {
    throw new DBBackupError( recording, 'Recording has no blob.' );
  }
  if (!blob.size) {
    throw new DBBackupError( recording, 'Recording blob has no size.' );
  }

  // Total size reported by the blob; the readable portion may turn out to be smaller
  const computedSize = blob.size;

  // Bytes successfully uploaded so far
  let recoveredSize = 0;

  // Read the first part before starting the upload, so an entirely unreadable blob
  // fails fast without creating an upload on the server.
  // NotFoundError is handled in readBlobPart (sets `truncated`); any other error propagates.
  if (verbose) console.log('Reading first part...');
  let currentPart = await readBlobPart( blob, 0, Math.min(partSize, computedSize) );
  let truncated   = currentPart.truncated;
  if (currentPart.buffer.byteLength === 0) {
    throw new DBBackupError( recording, 'Blob data is entirely unreadable (NotFoundError on first segment).' );
  }

  // Label the upload by what the bytes actually are (sets the S3 key's extension and ContentType)
  const filetype = detectVideoContentType( currentPart.buffer, blob.type );

  // Try and get a wakelock (keeps the screen awake while uploading)
  const wakeLock = await getOptionalWakeLock();
  if (verbose) console.log(`Got wakelock: ${wakeLock}`)

  // Try the main logic
  // IMPORTANT: Any error should be thrown. The wakelock is released in `finally`.
  try {

    // ---- Step 1: Initiate ---------------------------------------------------

    // Get the destination
    ({ uploadId, filename } = await initiateMultipartUpload(recording, filetype));
    if (!uploadId) {
      throw new Error('Could not create / retrieve an uploadID.')
    }

    // Save filename to local IndexedDB; track failure with Sentry but don't block.
    try {
      await updateRecording( recording.recordingID, { filename } );
    }
    catch (e) {
      trackError(ErrorState.UPDATE_STORED_RECORDING_METADATA_FAILED, e);
    }


    // ---- Step 2: Upload parts -----------------------------------------------

    // { ETag, PartNumber } — required by CompleteMultipartUpload
    const parts = [];

    // NOTE: AWS uses 1-indexing
    for (let partNumber = 1; currentPart !== null; partNumber++) {
      const partBytes = currentPart.buffer.byteLength;
      if (verbose) console.log(`Uploading part ${partNumber} (${partBytes} bytes)...`);

      // Upload the part, reporting progress relative to the full blob size
      const partOffset = recoveredSize;
      const etag = await uploadPartWithRetry(
        filename, uploadId, partNumber, currentPart.buffer, filetype,
        (loaded) => { if (onProgress) onProgress(partOffset + loaded, computedSize); },
        { attempts, retryDelayMs, verbose },
      );
      if (verbose) console.log(`Part ${partNumber} uploaded! (ETag: ${etag})`);

      parts.push({ ETag: etag, PartNumber: partNumber });
      recoveredSize += partBytes;

      // Drop the reference to this part's data before reading the next one
      currentPart = null;

      // Stop at the end of the blob, or at the first unreadable segment
      const nextStart = partNumber * partSize;
      if (truncated || nextStart >= computedSize) break;

      // Read the next part
      const nextPart = await readBlobPart( blob, nextStart, Math.min(nextStart + partSize, computedSize) );
      if (nextPart.truncated) truncated = true;
      if (nextPart.buffer.byteLength > 0) currentPart = nextPart;
    }

    if (verbose) {
      const readablePercent = (recoveredSize / computedSize * 100).toFixed(1);
      console.log(`Uploaded ${recoveredSize} of ${computedSize} bytes (~${readablePercent}%) in ${parts.length} part(s)${truncated ? ' — TRUNCATED' : ''}.`);
    }


    // ---- Step 3: Complete ---------------------------------------------------

    // Try calling the completion endpoint
    await completeMultipartUpload(filename, uploadId, parts, truncated, computedSize, recoveredSize);
    if (verbose) console.log(`Uploaded successfully!${ truncated ? ' (truncated)' : '' }`);

    // Update local IndexedDB with uploaded status.
    // If this fails, log the error but do not fail the upload.
    try {
      await updateRecording(
        recording.recordingID,
        { uploaded: true, uploadedAt: new Date().toISOString() }
      );
      if (verbose) console.log('Updated local recording object to reflect new upload.')
    }
    catch (err) {
      console.error("Failed to mark recording as uploaded in local storage:", err);
    }

    // Return the name of the file in AWS, and whether the upload was truncated
    return { filename, truncated, computedSize, recoveredSize };
  }

  // If any error raised, abort the upload and wrap in a tracked UploadError
  catch (e) {
    if (verbose) console.error(`Raised error attempting to upload: ${e}.`)

    // If an upload was started, make sure it's aborted.
    // A failed abort is logged, but must not mask the original error.
    if (uploadId) {
      try {
        if (verbose) console.log(`Aborting upload ${uploadId}`);
        await abortMultipartUpload(filename, uploadId);
        if (verbose) console.log(`Aborted upload ${uploadId} successfully.`);
      }
      catch (abortErr) {
        console.error(`Failed to abort upload ${uploadId}:`, abortErr);
      }
    }
    else {
      if (verbose) console.log(`No upload ID to abort.`)
    }

    // Wrap in an upload error, tracking "global" metadata about the recording itself
    throw new UploadError(e, {
      'recordingID':    recording.recordingID,
      'recordingSize':  computedSize,
      'recordingTitle': recording.title,
      'uploadID':       uploadId,
      'filenameAWS':    filename,
      'hasWakeLock':    wakeLock !== undefined && wakeLock !== null,
      'truncated':      truncated,
      'recoveredSize':  recoveredSize,
    })
  }

  // Always release the wakelock, if one was claimed.
  // Kept separate so a failed release (e.g. lock already released when the tab
  // was hidden) can never turn a finished upload into a reported failure.
  finally {
    if (wakeLock) {
      try {
        await wakeLock.release();
        if (verbose) console.log('Released wakelock.');
      }
      catch (err) {
        console.warn(`Failed to release WakeLock: ${err?.name}, ${err?.message}`);
      }
    }
  }
}


/**
 * Upload multiple recordings together.
 *
 * Each element in `recordings` will have the fields:
 *   - recordingID: unique ID in localdb
 *   - title: a local title for the file
 *   - blob: the actual object to upload
 *
 * @param {Array} recordings - Array of recording objects to upload
 * @param {Function} onProgress - Callback for progress updates (loaded, total)
 * @returns {Promise<Array>} - Promise that resolves with an array of results from each upload when all uploads succeed.
 * @throws {StateError} If one or more uploads fail.
 */
export async function uploadRecordingsBatch(recordings, onProgress, verbose = false) {
  if (verbose) console.debug('Uploading recordings: ', recordings )

  // Compute the combined total size of all blobs
  const totalSize = recordings.reduce((sum, recording) => {
    return sum + (recording.blob?.size || 0);
  }, 0);

  // Warn if computed size is 0 (or negative somehow)
  if (totalSize <= 0) {
    console.warn(`Computed total upload size as ${totalSize} bytes.`)
  } else {
    if (verbose) console.log('Total size: ', totalSize)
  }

  // Track progress for each upload
  const progressByRecording = new Map();
  recordings.forEach(recording => {
    progressByRecording.set(recording.recordingID, 0);
  });

  // Helper function to update aggregated progress
  // Note: If totalSize was computed at 0, sends a progress update indicating half the data is loaded.
  // TODO: Smarter totalSize computation based on individual upload calls
  const updateAggregatedProgress = () => {
    const totalLoaded = Array.from(progressByRecording.values()).reduce((sum, loaded) => sum + loaded, 0);
    if (onProgress) {
      onProgress(totalLoaded, totalSize <= 0 ? (totalLoaded * 2) : totalSize);
    }
  };

  // Create upload promises for each recording
  const uploadPromises = recordings.map((recording) => async () => {

    // Create a progress handler for this specific recording
    const recordingProgressHandler = (loaded, total) => {

      // Update this recording's progress
      progressByRecording.set(recording.recordingID, loaded);

      // Update the aggregated progress
      updateAggregatedProgress();
    };

    // Upload the recording
    try {
      const result = await uploadRecording(recording, recordingProgressHandler, verbose);
      if (verbose) console.log(`Successfully uploaded recording ${recording.recordingID}`)

      return {
        success: true,
        recordingId: recording.recordingID,
        filename: result?.filename,
        error: null,
      };
    }
    catch (error) {
      console.error(`Failed to upload recording ${recording.recordingID}:`, error);

      return {
        success: false,
        recordingId: recording.recordingID,
        filename: null,
        error: error,
      };
    }
  });

  // Execute uploads sequentially
  // TODO: Could potentially speed up with parallel execution, but we'd have to ensure the server assigns distinct names to all the files
  const uploadResults = [];
  for (const uploadPromise of uploadPromises) {
    uploadResults.push(await uploadPromise());
  }
  // TODO: Consider clearing successfully uploaded recordings from local storage
  // after successful upload. This would prevent re-uploading the same files.

  // Aggregate errors across all the uploads
  const failedUploads = uploadResults.filter(({success}) => !success);
  if (failedUploads.length) {
    throw new BatchUploadError( uploadResults );
  }

  // Return the set of results
  return uploadResults;
}



// ----------------------------------------------------------------------------
//   Testing
// ----------------------------------------------------------------------------


/**
 * Drop-in replacement for `uploadRecording` that simply reports
 * progress updates, without performing any actual upload logic.
 *
 * Useful for testing workflow without requiring an actual blob to upload.
 */
export async function spoofUploadRecording( recording, onProgress, throwError = false ) {

  // If an actual blob was passed, log it
  if (recording !== null) {
    console.log("Received recording for upload: ", recording)
  }

  // Parameters
  const numIncrements = 3;
  const delayMS       = 1000;

  // Loop and wait, simulating upload behavior
  for ( let i = 1; i <= numIncrements; i++ ) {
    await sleep( delayMS );
    onProgress( i, numIncrements );

    // Optionally throw an error midway through "uploading"
    if ( throwError && i >= 2 ) throw new Error('Dev error!');
  }

  // Final wait
  await sleep( delayMS );
}
